/**
 * The one bridge from the palette to code that needs a colour as a *value*
 * rather than a Tailwind class — inline SVG attributes, style objects, canvas.
 *
 * No colour is defined here. The palette lives in `src/index.css` as CSS
 * variables; this module only names them, so changing a channel there changes
 * the whole site, diagrams and certificates included. Never write a literal
 * colour in a component — reach for `color()` instead.
 *
 * Ported unchanged from the product app (APP/frontend/src/lib/theme.ts) so the
 * same token name means the same thing in both codebases.
 */

/**
 * A CSS colour string for a palette token, optionally at an alpha. Valid
 * anywhere CSS is parsed — including SVG presentation attributes such as
 * `fill` and `stroke` on inline SVG.
 *
 * The alpha argument is why this exists rather than a plain string constant:
 * the dark site used to build faded colours by concatenating a hex suffix
 * (`${c}66`), which a CSS variable cannot do.
 *
 * @param {ColorToken} token
 * @param {number} [alpha] 0–1
 * @returns {string}
 */
export function color(token, alpha) {
  return alpha === undefined ? `rgb(var(--${token}))` : `rgb(var(--${token}) / ${alpha})`;
}

/**
 * A resolved `#rrggbb` string, for the few places that need a real hex — a
 * canvas export, a library that parses colour itself, an `<meta>` tag.
 *
 * Resolves from computed style, so **call it at render time, not at module
 * scope**: a module-level constant freezes whichever scheme was live at import
 * and stops following a later flip to `class="dark"`.
 *
 * @param {ColorToken} token
 * @param {string} fallback used when there is no live document to read from
 * @returns {string}
 */
export function colorHex(token, fallback) {
  if (typeof document === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(`--${token}`).trim();
  const channels = raw.split(/[\s,/]+/).slice(0, 3).map(Number);
  if (channels.length < 3 || channels.some((n) => Number.isNaN(n))) return fallback;
  return `#${channels.map((n) => Math.round(n).toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Every token the palette defines, for reference and for editor completion.
 *
 * @typedef {'ink'|'ink-deep'|'panel'|'panel-raised'
 *   |'line'|'line-soft'|'line-bright'
 *   |'brand'|'brand-soft'|'brand-deep'
 *   |'gold'|'gold-bright'
 *   |'content'|'content-muted'|'content-faint'
 *   |'valid'|'danger'|'warn'
 *   |'framework-iso'|'framework-nist'|'framework-eu'|'framework-uae'|'framework-ksa'
 *   |'chart-1'|'chart-2'|'chart-3'|'chart-4'|'chart-5'
 *   |'logo-a-from'|'logo-a-to'|'logo-i-from'|'logo-i-to'|'logo-letter'
 *   |'qr-dark'|'qr-light'|'scrim'|'sheen'} ColorToken
 */

/**
 * The categorical ramp, in the order it should be used. A diagram that needs
 * "some distinct colours" and means nothing by them takes them from here —
 * the status palette means pass/fail and must not be borrowed for decoration.
 *
 * @type {ColorToken[]}
 */
export const CHART_SERIES = ["chart-1", "chart-2", "chart-3", "chart-4", "chart-5"];
